import { useMemo, FC, useState } from 'react';
import { useQuery } from 'react-query';
import { IInvalidPos, IInvalidPosSort, ISortDirection, IResponse } from '../../../models';
import { getInvalidPos } from '../../../fetch';
import { Modal, Table, Pagination } from '../../../components';
import { formatDate } from '../../../helpers';
import { Typography, Box } from '@mui/material';

interface IInvalidPosModal {
  isOpen: boolean;
  onClose: () => void;
}

export const InvalidPosModal: FC<IInvalidPosModal> = ({ isOpen, onClose }) => {
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('PurchaseOrderNumber');
  const [sortDirection, setSortDirection] = useState<{
    PurchaseOrderNumber?: ISortDirection;
    Partner?: ISortDirection;
    OrderDate?: ISortDirection;
    JobId?: ISortDirection;
    TaskId?: ISortDirection;
    Description?: ISortDirection;
  }>({
    PurchaseOrderNumber: 'Asc'
  });
  const { isLoading, data: invalidPosData } = useQuery<IResponse<IInvalidPos[]>, Error>(
    ['getInvalidPos', page, perPage, selectedSort, sortDirection],
    () =>
      getInvalidPos({
        page: page + 1,
        perPage,
        sortBy: selectedSort as IInvalidPosSort,
        sortDirection: sortDirection[selectedSort] as ISortDirection
      }),
    {
      onSuccess: d => {
        setRecordCount(d.totalRecordCount);
      }
    }
  );

  const data = useMemo(() => invalidPosData?.records ?? [], [invalidPosData]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Order Number',
        accessor: 'purchaseOrderNumber',
        isServerSorted: selectedSort === 'PurchaseOrderNumber',
        isServerSortedDesc: sortDirection.PurchaseOrderNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('PurchaseOrderNumber')
      },
      {
        Header: 'Vendor',
        accessor: 'partner',
        isServerSorted: selectedSort === 'Partner',
        isServerSortedDesc: sortDirection.Partner === 'Desc',
        handleClickColumn: () => handleClickColumn('Partner')
      },
      {
        Header: 'Job',
        accessor: 'jobId',
        isServerSorted: selectedSort === 'JobId',
        isServerSortedDesc: sortDirection.JobId === 'Desc',
        handleClickColumn: () => handleClickColumn('JobId')
      },
      {
        Header: 'Task',
        accessor: 'taskId',
        isServerSorted: selectedSort === 'TaskId',
        isServerSortedDesc: sortDirection.TaskId === 'Desc',
        handleClickColumn: () => handleClickColumn('TaskId')
      },
      {
        Header: 'Description',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description')
      },
      {
        Header: 'Order Date',
        accessor: 'orderDate',
        isServerSorted: selectedSort === 'OrderDate',
        isServerSortedDesc: sortDirection.OrderDate === 'Desc',
        handleClickColumn: () => handleClickColumn('OrderDate'),
        Cell: props => <>{formatDate(props.cell.row.original.orderDate)}</>
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Modal open={isOpen} onClose={onClose} maxWidth="lg" title='"Invalid Purchse Orders"'>
      <Box mt={1} mb={2}>
        <Typography>
          Invalid Purchase Orders are POs where either there is no template set up in BuilderLinq
          for the specific Task ID, OR there is no Job configured in BuilderLinq for the Job Code
        </Typography>
      </Box>
      <Table columns={columns} data={data} isLoading={isLoading} hidePagination />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </Modal>
  );
};
